/**
 * PrimateAuthContext — provides auth state to PrimateVision and related components.
 *
 * This context exists to solve a React hook rules problem: PrimateVision renders
 * both inside and outside Clerk's ClerkProvider (when Clerk is not configured,
 * AppRoutesNoAuth renders PrimateVision without ClerkProvider). Calling useAuth()
 * inside a try/catch violates hook rules.
 *
 * Solution: ClerkAuthProvider (only rendered inside ClerkProvider) reads Clerk state
 * and populates this context. AppRoutesNoAuth populates the context with defaults.
 * usePrimateAuth() reads from this context — no direct Clerk hook calls needed.
 */
import React, { createContext, useState, useEffect } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { probeAccessRestriction } from '../../core/lib/api/misc';

const ADMIN_EMAILS = [
  'matt@primateintelligence.ai',
  'mehdi@primateintelligence.ai',
];

export function hasAcceptedTerms(): boolean {
  return !!localStorage.getItem('primate-terms-accepted');
}

export interface PrimateAuthState {
  isSignedIn: boolean;
  isPro: boolean;
  authLoaded: boolean;
  isAdmin: boolean;
  isRestricted: boolean;
  getToken: (options?: { template?: string }) => Promise<string | null>;
  showIntercomHint: boolean;
  setShowIntercomHint: (v: boolean) => void;
  demoUsageCount: number;
  setDemoUsageCount: (v: number) => void;
}

const DEFAULT_AUTH: PrimateAuthState = {
  isSignedIn: false,
  isPro: false,
  authLoaded: true, // treat as loaded so UI doesn't spin forever
  isAdmin: false,
  isRestricted: false,
  getToken: async () => null,
  showIntercomHint: false,
  setShowIntercomHint: () => {},
  demoUsageCount: 0,
  setDemoUsageCount: () => {},
};

const PrimateAuthContext = createContext<PrimateAuthState>(DEFAULT_AUTH);

/**
 * Inner component — safe to call Clerk hooks because it's ONLY rendered inside
 * ClerkProvider (via AppRoutesWithAuth which is always wrapped in ClerkProvider).
 */
function ClerkAuthConsumer({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const { user } = useUser();

  const [showIntercomHint, setShowIntercomHint] = useState(() => !sessionStorage.getItem('pv-hint-shown'));
  const [demoUsageCount, setDemoUsageCount] = useState(() =>
    parseInt(localStorage.getItem('primate-demo-count-' + new Date().toDateString()) ?? '0', 10)
  );
  const [isRestricted, setIsRestricted] = useState(false);

  useEffect(() => {
    if (!showIntercomHint) return;
    const t = setTimeout(() => {
      setShowIntercomHint(false);
      sessionStorage.setItem('pv-hint-shown', '1');
    }, 10000);
    return () => clearTimeout(t);
  }, [showIntercomHint]);

  // Dev/test bypass — allows Playwright tests to simulate signed-in state without a real Clerk
  // session. Same pattern as primate-dev-auth in PasswordGate.tsx.
  const devSignedIn = localStorage.getItem('primate-dev-signed-in') === '1';
  const devPlan = localStorage.getItem('primate-dev-plan');      // 'pro' | 'free' | null
  const devRestricted = localStorage.getItem('primate-dev-restricted') === '1';
  const devAdmin = localStorage.getItem('primate-dev-admin') === '1';
  const authLoaded = (auth.isLoaded ?? false) || devSignedIn;
  const isSignedIn = ((auth.isLoaded && auth.isSignedIn) ?? false) || devSignedIn;
  let isPro = (user?.publicMetadata as { plan?: string } | undefined)?.plan === 'pro' || false;
  const userEmail =
    user?.primaryEmailAddress?.emailAddress ??
    user?.emailAddresses?.[0]?.emailAddress ?? '';
  const isAdmin = devSignedIn ? devAdmin : (isSignedIn && ADMIN_EMAILS.includes(userEmail));
  if (isAdmin) isPro = true;
  // Apply dev overrides when bypass is active
  if (devSignedIn) {
    isPro = devPlan === 'pro' || devAdmin;
  }

  // Check restriction status once when a real Clerk session loads.
  // We probe /v1/usage: restricted users get 403 access_restricted; approved users get 200.
  // Uses auth.getToken() directly to avoid any dependency on the module-level token getter.
  useEffect(() => {
    if (!auth.isLoaded || !auth.isSignedIn || devSignedIn) {
      setIsRestricted(false);
      return;
    }
    let cancelled = false;
    // R15 (PRI-439): endpoint literal moved to the API layer.
    auth.getToken().then(async token => {
      if (!token || cancelled) return;
      const status = await probeAccessRestriction(token);
      if (cancelled) return;
      if (status === 'restricted') setIsRestricted(true);
      else if (status === 'ok') setIsRestricted(false);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [auth.isSignedIn, auth.isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const value: PrimateAuthState = {
    isSignedIn,
    isPro,
    authLoaded,
    isAdmin,
    isRestricted: devSignedIn ? (isSignedIn && !devAdmin && devRestricted) : (isSignedIn && !isAdmin && isRestricted),
    // When devSignedIn bypass is active, use a no-op getToken so tests don't hang waiting for Clerk.
    getToken: devSignedIn ? async () => null : auth.getToken,
    showIntercomHint,
    setShowIntercomHint,
    demoUsageCount,
    setDemoUsageCount,
  };

  return (
    <PrimateAuthContext.Provider value={value}>
      {children}
    </PrimateAuthContext.Provider>
  );
}

/**
 * NoAuthProvider — used by AppRoutesNoAuth to provide defaults without Clerk.
 * Auth state is always "signed out, loaded".
 * Dev/test bypass: set localStorage 'primate-dev-signed-in' = '1' to simulate
 * a signed-in user (same pattern as ClerkAuthConsumer — works even without Clerk).
 */
export function NoAuthProvider({ children }: { children: React.ReactNode }) {
  const [showIntercomHint, setShowIntercomHint] = useState(() => !sessionStorage.getItem('pv-hint-shown'));
  const [demoUsageCount, setDemoUsageCount] = useState(() =>
    parseInt(localStorage.getItem('primate-demo-count-' + new Date().toDateString()) ?? '0', 10)
  );

  useEffect(() => {
    if (!showIntercomHint) return;
    const t = setTimeout(() => {
      setShowIntercomHint(false);
      sessionStorage.setItem('pv-hint-shown', '1');
    }, 10000);
    return () => clearTimeout(t);
  }, [showIntercomHint]);

  // Dev/test bypass — mirrors ClerkAuthConsumer behaviour so Playwright tests work
  // without a real Clerk session, regardless of whether Clerk is configured.
  const devSignedIn = localStorage.getItem('primate-dev-signed-in') === '1';
  const devPlan = localStorage.getItem('primate-dev-plan');      // 'pro' | 'free' | null
  const devRestricted = localStorage.getItem('primate-dev-restricted') === '1';
  const devAdmin = localStorage.getItem('primate-dev-admin') === '1';

  const devIsPro = devSignedIn ? (devPlan === 'pro' || devAdmin) : false;
  const devIsAdmin = devSignedIn ? devAdmin : false;
  const devIsRestricted = devSignedIn ? (!devAdmin && devRestricted) : false;

  const value: PrimateAuthState = {
    isSignedIn: devSignedIn,
    isPro: devIsPro,
    authLoaded: true,
    isAdmin: devIsAdmin,
    isRestricted: devIsRestricted,
    getToken: async () => null,
    showIntercomHint,
    setShowIntercomHint,
    demoUsageCount,
    setDemoUsageCount,
  };

  return (
    <PrimateAuthContext.Provider value={value}>
      {children}
    </PrimateAuthContext.Provider>
  );
}

/**
 * ClerkAuthProvider — wraps children with live Clerk-derived auth state.
 * Must only be rendered inside a ClerkProvider.
 */
export function ClerkAuthProvider({ children }: { children: React.ReactNode }) {
  return <ClerkAuthConsumer>{children}</ClerkAuthConsumer>;
}

export { PrimateAuthContext };
